import { Card, Layout, Spinner } from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import * as Illustration from "@npco/zeller-design-system/illustration";
import { ADT } from "pb.adt";

import { Layout as ModalLayout } from "../../shared/layout";
import { Prompt } from "../../shared/prompt";
import type { Page } from "./page";
import { PaymentFlow } from "./render/payment-flow";
import { PaymentMethodLinkEmail } from "./render/payment-method-link-email";
import { PaymentMethodLinkSMS } from "./render/payment-method-link-sms";

const defaultLayoutProps = {
	actionsStyle: "full-width",
	logo: true,
} satisfies ModalLayout.Props;

export function render(page: Page) {
	return ADT.switch(page, {
		Initialising: (page) => (
			<ModalLayout {...defaultLayoutProps} onClose={page.onClose}>
				<Prompt image={<Spinner size="lg" />} title="Connecting" />
			</ModalLayout>
		),
		ErrorSetupRequired: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				onClose={page.onClose}
				actions={[{ label: "Close", onClick: page.onClose, primary: true }]}
			>
				<Prompt
					image={<Illustration.Error />}
					title="Configuration Failure"
					message="Please run Setup to check your configuration."
				/>
			</ModalLayout>
		),
		ErrorNetworkFailure: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				onClose={page.onClose}
				actions={[
					{ label: "Try Again", onClick: page.onRetry, primary: true },
					{ label: "Close", onClick: page.onClose },
				]}
			>
				<Prompt
					image={<Illustration.Error />}
					title="Network Failure"
					message="Please check your network connection."
				/>
			</ModalLayout>
		),
		ErrorOperationFailure: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				onClose={page.onClose}
				actions={[
					{ label: "Try Again", onClick: page.onRetry, primary: true },
					{ label: "Close", onClick: page.onClose },
				]}
			>
				<Prompt
					image={<Illustration.Error />}
					title="Operation Failure"
					message="An unrecoverable error occurred when processing this transaction. Please try again."
				/>
			</ModalLayout>
		),
		PaymentMethodSelection: (page) => (
			<ModalLayout {...defaultLayoutProps} onClose={page.onClose}>
				<Layout.Container center className="p-32">
					<Layout.Article fixed center>
						<h1 className="text-2xl/heading text-center">
							Select Payment Method
						</h1>
						<Layout.Section>
							<Card
								size="lg"
								title="Send Payment Link via SMS"
								avatar={{ icon: <Icon.ChatBubble />, color: false }}
								onClick={page.onLinkSMS}
							/>
							<Card
								size="lg"
								title="Send Payment Link via Email"
								avatar={{ icon: <Icon.Envelope />, color: false }}
								onClick={page.onLinkEmail}
							/>
						</Layout.Section>
					</Layout.Article>
				</Layout.Container>
			</ModalLayout>
		),
		PaymentMethodLinkEmail: (page) => (
			<PaymentMethodLinkEmail
				page={page}
				defaultLayoutProps={defaultLayoutProps}
			/>
		),
		PaymentMethodLinkSMS: (page) => (
			<PaymentMethodLinkSMS
				page={page}
				defaultLayoutProps={defaultLayoutProps}
			/>
		),
		PaymentFlow: (page) => (
			<PaymentFlow page={page} defaultLayoutProps={defaultLayoutProps} />
		),
		PaymentExpired: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				actions={[{ label: "Close", onClick: page.onClose, primary: true }]}
			>
				<Prompt image={<Illustration.Error />} title="Payment Expired" />
			</ModalLayout>
		),
		PaymentCancelled: (page) => (
			<ModalLayout
				{...defaultLayoutProps}
				actions={[{ label: "Close", onClick: page.onClose, primary: true }]}
			>
				<Prompt
					image={<Illustration.SuccessThumbsUp />}
					title="Payment Cancelled"
				/>
			</ModalLayout>
		),
	});
}
